import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useApi } from "@/lib/api";

const OUTCOME_TOAST = {
    connected: ["success", "TikTok connected."],
    cancelled: ["message", "TikTok was not connected."],
    error: ["error", "Could not connect TikTok. Try again."],
};

/**
 * "Connect TikTok" through TikTok Login Kit. The browser goes to TikTok and
 * comes back to the same page with ?tiktok=connected|cancelled|error, which
 * is shown once and removed from the URL. Renders nothing when the server
 * has no TikTok keys. `onStatus` gets the connection status on every load.
 */
export default function ConnectTikTok({ onStatus, compact = false }) {
    const { request } = useApi();
    const [params, setParams] = useSearchParams();
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await request("/tiktok/status");
            setStatus(res);
            onStatus?.(res);
        } catch {
            setStatus({ configured: false, connected: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const outcome = params.get("tiktok");
        if (!outcome) return;
        const [kind, text] = OUTCOME_TOAST[outcome] || OUTCOME_TOAST.error;
        toast[kind](text);
        const next = new URLSearchParams(params);
        next.delete("tiktok");
        setParams(next, { replace: true });
    }, [params, setParams]);

    const connect = async () => {
        setBusy(true);
        try {
            const res = await request("/tiktok/connect", {
                method: "POST",
                body: { return_to: window.location.pathname + window.location.search },
            });
            window.location.assign(res.url);
        } catch (err) {
            toast.error(err?.message || "Could not start TikTok sign in.");
            setBusy(false);
        }
    };

    const disconnect = async () => {
        setBusy(true);
        try {
            await request("/tiktok/disconnect", { method: "POST" });
            toast("TikTok disconnected.");
            await load();
        } catch (err) {
            toast.error(err?.message || "Could not disconnect TikTok.");
        } finally {
            setBusy(false);
        }
    };

    if (!status || !status.configured) return null;

    if (status.connected && !status.needs_reconnect) {
        return (
            <div
                data-testid="tiktok-connected"
                className="flex items-center justify-between gap-3 flex-wrap"
            >
                <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-signal" strokeWidth={2.5} />
                    <span>
                        Connected as <span className="mono">@{status.username}</span>
                    </span>
                </div>
                <button
                    type="button"
                    data-testid="tiktok-disconnect"
                    onClick={disconnect}
                    disabled={busy}
                    className="text-xs text-black/50 underline underline-offset-4 disabled:opacity-30"
                >
                    Disconnect
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <button
                type="button"
                data-testid="tiktok-connect"
                onClick={connect}
                disabled={busy}
                className="samoony-pill w-full sm:w-auto border border-black/15 bg-white disabled:opacity-30"
            >
                {busy ? "Opening TikTok..." : status.needs_reconnect ? "Reconnect TikTok" : "Connect TikTok"}
            </button>
            {!compact && (
                <p className="text-xs text-black/50">
                    TikTok asks you to approve. We only read your profile and public videos.
                    We never post for you.
                </p>
            )}
        </div>
    );
}
